package co.uk.sacursoftware.ssinventory.ui

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import co.uk.sacursoftware.ssinventory.data.model.*
import co.uk.sacursoftware.ssinventory.data.remote.SSInventoryApi
import co.uk.sacursoftware.ssinventory.data.repository.SSInventoryRepository
import co.uk.sacursoftware.ssinventory.util.FirebaseStorageService
import co.uk.sacursoftware.ssinventory.util.StorageArea
import co.uk.sacursoftware.ssinventory.util.rememberPlatformImagePicker
import co.uk.sacursoftware.ssinventory.util.getCurrentLocalDate
import kotlinx.coroutines.launch
import kotlinx.datetime.*
import androidx.compose.foundation.Image
import androidx.compose.ui.layout.ContentScale
import org.jetbrains.compose.resources.painterResource
import ssinventory.shared.generated.resources.*

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun DeliveryScreen(
    onBack: () -> Unit,
    onNavigateToImageViewer: (List<String>, Int, String?) -> Unit
) {
    val scope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }
    val repository = remember { SSInventoryRepository(SSInventoryApi(SSInventoryApi.createDefaultClient())) }
    val storageService = remember { FirebaseStorageService() }

    val today = getCurrentLocalDate()
    val dates = remember {
        (-3..3).map { i ->
            today.plus(i, DateTimeUnit.DAY)
        }
    }

    var selectedDate by remember { mutableStateOf(today) }
    var deliveries by remember { mutableStateOf<List<Delivery>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var isUploading by remember { mutableStateOf(false) }

    var deliveryToDelete by remember { mutableStateOf<Delivery?>(null) }

    // Verification confirmation dialog state
    var pendingVerificationResult by remember { mutableStateOf<ReceiptVerificationResult?>(null) }
    var pendingImageUrl by remember { mutableStateOf<String?>(null) }
    var editableTitle by remember { mutableStateOf("") }
    var editablePlatform by remember { mutableStateOf("") }
    var showVerificationDialog by remember { mutableStateOf(false) }

    fun loadDeliveries(dateStr: String) {
        scope.launch {
            isLoading = true
            repository.getDeliveries(dateStr).onSuccess { list ->
                deliveries = list
            }.onFailure {
                deliveries = emptyList()
            }
            isLoading = false
        }
    }

    LaunchedEffect(selectedDate) {
        loadDeliveries(selectedDate.toString())
    }

    val imagePicker = rememberPlatformImagePicker { bytes, _ ->
        scope.launch {
            isUploading = true
            try {
                val url = storageService.uploadImage(bytes, StorageArea.DELIVERIES)
                pendingImageUrl = url
                repository.verifyReceipt(VerifyReceiptRequest(imageUrl = url)).onSuccess { result ->
                    if (result.error != null) {
                        snackbarHostState.showSnackbar("Server error: ${result.error}")
                    } else {
                        pendingVerificationResult = result
                        editableTitle = result.orderId ?: "Receipt $selectedDate"
                        editablePlatform = result.platform ?: ""
                        showVerificationDialog = true
                    }
                }.onFailure { e ->
                    snackbarHostState.showSnackbar("Verification failed: ${e.message}")
                }
            } catch (e: Exception) {
                snackbarHostState.showSnackbar("Upload failed: ${e.message}")
            }
            isUploading = false
        }
    }

    Scaffold(
        snackbarHost = { SnackbarHost(snackbarHostState) },
        topBar = {
            TopAppBar(
                title = { ScreenTitle("Deliveries") },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        BackArrowIcon()
                    }
                }
            )
        }
    ) { padding ->
        Box(modifier = Modifier.padding(padding).fillMaxSize()) {
            val collapse = rememberScrollCollapse()
            val listState = rememberLazyListState()
            val nested = remember(listState, collapse) {
                collapse.connection {
                    listState.firstVisibleItemIndex == 0 && listState.firstVisibleItemScrollOffset == 0
                }
            }
            Column(modifier = Modifier.fillMaxSize()) {
                CollapsingDateTabs(
                    dates = dates,
                    selectedDate = selectedDate,
                    today = today,
                    collapse = collapse.fraction,
                    onDateSelected = { selectedDate = it },
                    onExpand = { collapse.expand() }
                )

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {

                if (isUploading) {
                    Box(
                        modifier = Modifier.fillMaxWidth().height(48.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            CircularProgressIndicator(modifier = Modifier.size(20.dp), color = MaterialTheme.colorScheme.primary)
                            Text("Uploading & Verifying Receipt...", style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold)
                        }
                    }
                } else {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Button(
                            onClick = { imagePicker.takePhoto() },
                            modifier = Modifier.weight(1f).height(48.dp),
                            shape = MaterialTheme.shapes.medium,
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                        ) {
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Image(
                                    painter = painterResource(Res.drawable.delivery),
                                    contentDescription = "Picture",
                                    modifier = Modifier.size(20.dp),
                                    contentScale = ContentScale.Fit
                                )
                                Text("Picture", fontWeight = FontWeight.Bold)
                            }
                        }

                        Button(
                            onClick = { imagePicker.pickFromGallery() },
                            modifier = Modifier.weight(1f).height(48.dp),
                            shape = MaterialTheme.shapes.medium,
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.secondary)
                        ) {
                            Row(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalAlignment = Alignment.CenterVertically) {
                                Image(
                                    painter = painterResource(Res.drawable.folder),
                                    contentDescription = "Gallery",
                                    modifier = Modifier.size(20.dp),
                                    contentScale = ContentScale.Fit
                                )
                                Text("Gallery", fontWeight = FontWeight.Bold)
                            }
                        }
                    }
                }

                if (isLoading) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        BrandLoadingSpinner()
                    }
                } else if (deliveries.isEmpty()) {
                    Box(modifier = Modifier.fillMaxWidth().weight(1f), contentAlignment = Alignment.Center) {
                        Text("No delivery receipts for this date.", style = MaterialTheme.typography.bodyMedium, color = MaterialTheme.colorScheme.outline)
                    }
                } else {
                    LazyColumn(
                        state = listState,
                        modifier = Modifier.fillMaxWidth().weight(1f).nestedScroll(nested),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        items(items = deliveries, key = { it.id }) { delivery ->
                            Card(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .clickable {
                                        if (delivery.imageUrl.isNotBlank()) {
                                            onNavigateToImageViewer(listOf(delivery.imageUrl), 0, delivery.title)
                                        }
                                    },
                                shape = MaterialTheme.shapes.medium,
                                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface),
                                border = BorderStroke(1.dp, MaterialTheme.colorScheme.outline.copy(alpha = 0.3f))
                            ) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(12.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Column(
                                        modifier = Modifier.weight(1f),
                                        verticalArrangement = Arrangement.spacedBy(4.dp)
                                    ) {
                                        Text(
                                            text = delivery.title,
                                            style = MaterialTheme.typography.titleMedium,
                                            fontWeight = FontWeight.Bold,
                                            color = MaterialTheme.colorScheme.onSurface
                                        )
                                        if (!delivery.platform.isNullOrBlank()) {
                                            Text(
                                                text = "Platform: ${delivery.platform}",
                                                style = MaterialTheme.typography.bodyMedium,
                                                color = MaterialTheme.colorScheme.primary,
                                                fontWeight = FontWeight.SemiBold
                                            )
                                        }
                                        Text(
                                            text = "Saved: ${delivery.createdAt ?: delivery.date}",
                                            style = MaterialTheme.typography.bodySmall,
                                            color = MaterialTheme.colorScheme.secondary
                                        )
                                    }

                                    IconButton(onClick = { deliveryToDelete = delivery }) {
                                        CloseIcon()
                                    }
                                }
                            }
                        }
                    }
                }
                }
            }

            // Verification Confirmation Popup Dialog
            if (showVerificationDialog && pendingVerificationResult != null) {
                AlertDialog(
                    onDismissRequest = {
                        showVerificationDialog = false
                        pendingImageUrl = null
                        pendingVerificationResult = null
                    },
                    title = {
                        Text(
                            text = if (pendingVerificationResult!!.isReadable) "Verify Receipt" else "Receipt Unclear / Unreadable",
                            fontWeight = FontWeight.Bold
                        )
                    },
                    text = {
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            if (!pendingVerificationResult!!.isReadable && !pendingVerificationResult!!.errorMessage.isNullOrBlank()) {
                                Text(
                                    text = "Warning: ${pendingVerificationResult!!.errorMessage}",
                                    color = MaterialTheme.colorScheme.error,
                                    style = MaterialTheme.typography.bodyMedium
                                )
                            }

                            if (!pendingImageUrl.isNullOrBlank()) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth()
                                        .height(160.dp),
                                    contentAlignment = Alignment.Center
                                ) {
                                    RemoteImageView(
                                        url = pendingImageUrl!!,
                                        modifier = Modifier.fillMaxSize()
                                    )
                                }
                            }

                            OutlinedTextField(
                                value = editablePlatform,
                                onValueChange = { editablePlatform = it },
                                label = { Text("Platform (e.g. Deliveroo, UberEats)") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )

                            OutlinedTextField(
                                value = editableTitle,
                                onValueChange = { editableTitle = it },
                                label = { Text("Order ID / Number") },
                                modifier = Modifier.fillMaxWidth(),
                                singleLine = true
                            )
                        }
                    },
                    confirmButton = {
                        Button(
                            onClick = {
                                val url = pendingImageUrl
                                val title = editableTitle.ifBlank { "Receipt $selectedDate" }
                                val platform = editablePlatform.ifBlank { null }
                                showVerificationDialog = false
                                pendingImageUrl = null
                                pendingVerificationResult = null

                                if (url != null) {
                                    scope.launch {
                                        isUploading = true
                                        repository.saveDelivery(
                                            SaveDeliveryRequest(
                                                date = selectedDate.toString(),
                                                title = title,
                                                platform = platform,
                                                imageUrl = url
                                            )
                                        ).onSuccess {
                                            loadDeliveries(selectedDate.toString())
                                        }.onFailure {
                                            snackbarHostState.showSnackbar("Failed to save delivery receipt")
                                        }
                                        isUploading = false
                                    }
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.primary)
                        ) {
                            Text("Approve & Save", fontWeight = FontWeight.Bold)
                        }
                    },
                    dismissButton = {
                        TextButton(
                            onClick = {
                                showVerificationDialog = false
                                pendingImageUrl = null
                                pendingVerificationResult = null
                            }
                        ) {
                            Text("Cancel")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }

            deliveryToDelete?.let { delivery ->
                AlertDialog(
                    onDismissRequest = { deliveryToDelete = null },
                    title = { Text("Delete Delivery") },
                    text = { Text("Are you sure you want to delete receipt '${delivery.title}'?") },
                    confirmButton = {
                        Button(
                            onClick = {
                                val del = delivery
                                deliveryToDelete = null
                                deliveries = deliveries.filter { it.id != del.id }
                                scope.launch {
                                    repository.deleteDelivery(del.id)
                                }
                            },
                            colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error)
                        ) {
                            Text("Delete")
                        }
                    },
                    dismissButton = {
                        TextButton(onClick = { deliveryToDelete = null }) {
                            Text("Cancel")
                        }
                    },
                    shape = MaterialTheme.shapes.large
                )
            }
        }
    }
}
